import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApprovalFlows } from '@/accessControl/hooks/useApprovalFlows.hooks';
import { useApprovalFlowsEnabled } from '@/accessControl/hooks/useApprovalFlowsEnabled.hook';
import { useProductionVersionStatus } from '@/accessControl/hooks/useProductionVersionStatus.hooks';
import { useLockAndVersion } from '@/accessControl/useLockAndVersion.hook';
import { sqWorkbenchStore, sqWorkbookStore } from '@/core/core.stores';
import { flux } from '@/core/flux.module';
import { PUSH_IGNORE } from '@/core/flux.service';
import { useFluxPaths } from '@/core/hooks/useFluxPaths.hook';
import { useNonProductionContentSources } from '@/reportEditor/hooks/useNonProductionContentSources.hooks';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { TRACK } from '@/track/track.constants';
import { doTrack } from '@/track/track.service';
import { errorToast, successToast } from '@/utilities/toast.utilities';
import { useWorkbookIsTopic } from '@/workbook/WorkbookIsTopic.context';
import { useGetWorkbook } from '@/workbook/hooks/useGetWorkbook.hook';
import { normalizeGqlApprovalFlowData } from '@/worksheet/approvalFlowNormalization.utilities';
import { useApiCreateApprovalFlowAssignmentMutation } from '@seeqdev/api/endpoint/ApprovalFlowAssignmentsApi';
import { useApiCreateApprovalFlowAttachmentMutation } from '@seeqdev/api/endpoint/ApprovalFlowAttachmentsApi';
import { GetVersionHistoriesQueryKey } from '@seeqdev/api/endpoint/AuditApi';
import type { UseApprovalFlowsResult } from '@/accessControl/hooks/useApprovalFlows.hooks';
import type { VersionState } from '@/core/hooks/useVersionState.hook';
import type { WorkbookApprovalFlow } from '@/worksheet/worksheet.types';
import type { ApprovalFlowAssignmentOutputV1 } from '@seeqdev/api/model/models';

type ApprovalFlowHeaderController = Pick<
  UseApprovalFlowsResult,
  'approvalFlow' | 'approvalFlows' | 'nextStep' | 'futureStep' | 'showApplyBtn' | 'showStepDropdown' | 'approved'
> & {
  activeModal: null | 'FLOW_MODAL' | 'STEP_MODAL';
  approvalFlowsEnabled: boolean;
  isLocked: boolean;
  itemType: string;
  isInProd: boolean;
  isAttaching: boolean;
  isAssigning: boolean;
  isResolvingProductionStatus: boolean;
  hasLockingFailedPreviouslyDueToGlobalItems: boolean;
  applyApprovalFlow: (flow: WorkbookApprovalFlow | null) => Promise<void>;
  approveStep: (step: NonNullable<UseApprovalFlowsResult['nextStep']>, versionState?: VersionState) => Promise<void>;
  openApply: () => void;
  openApprove: () => void;
  openViewSteps: () => void;
  closeModal: () => void;
  didAttachFlow: () => boolean;
};

/** Handles approval actions and refreshes the header. Locks the workbook before approval when needed. */
export const useApprovalFlowHeaderController = ({
  onOpenApply,
  onAttachmentComplete,
}: {
  onOpenApply: () => void;
  onAttachmentComplete: () => void;
}): ApprovalFlowHeaderController => {
  const { t } = useTranslation();
  // Only this header opens these dialogs. Local state keeps just one open at a time.
  const [activeModal, setActiveModal] = useState<ApprovalFlowHeaderController['activeModal']>(null);
  // The dialog close callback may have old state. This ref tells it whether Apply succeeded.
  const attachmentSucceeded = useRef(false);
  const { approvalFlow, approvalFlows, nextStep, futureStep, showApplyBtn, showStepDropdown, workbookId, approved } =
    useApprovalFlows();
  const { isLocked } = useFluxPaths(sqWorkbookStore, ['isLocked']);
  const { stateParams } = useFluxPaths(sqWorkbenchStore, ['stateParams']);
  const { worksheetId, workstepId } = stateParams;
  const approvalFlowsEnabled = useApprovalFlowsEnabled();
  const isTopic = useWorkbookIsTopic();
  const queryClient = useQueryClient();
  const itemType = isTopic ? t('APPROVAL_FLOWS.ITEM_TYPES.TOPIC') : t('APPROVAL_FLOWS.ITEM_TYPES.ANALYSIS');
  const { inProductionItemIds, isFetching, hasResult } = useProductionVersionStatus([workbookId], {
    enabled: Boolean(approvalFlowsEnabled && approvalFlow && (showApplyBtn || showStepDropdown)),
  });
  const isInProd = inProductionItemIds.has(workbookId);
  const { affectedContentCount } = useNonProductionContentSources();
  // The last step is the one that labels the Topic In Production. Refuse it when any of the document's sources are
  // not themselves in production.
  const isFinalApprovalStep = Boolean(nextStep) && !futureStep;
  const holdsFinalApprovalForContent = isTopic && isFinalApprovalStep;
  const { fetch: fetchWorkbook } = useGetWorkbook({
    workbookId,
    worksheetId,
    workstepId,
    skipWorksheets: headlessRenderMode(),
  });

  const refreshApprovalFlow = async (): Promise<void> => {
    try {
      const { data } = await fetchWorkbook();
      const flowData = normalizeGqlApprovalFlowData(data?.workbook);
      flux.dispatch(
        'WORKBOOK_SET_APPROVAL_FLOW',
        {
          approvalFlows: flowData.approvalFlows,
          approvalFlowAssignments: flowData.approvalFlowAssignments,
          attachments: flowData.attachments,
        },
        PUSH_IGNORE,
      );
    } catch (error) {
      errorToast({ httpResponseOrError: error });
    }
  };
  const { mutate: createApprovalFlowAttachment, isPending: isAttaching } = useApiCreateApprovalFlowAttachmentMutation({
    onSuccess: async (_data, [, { flowId }]) => {
      attachmentSucceeded.current = true;
      doTrack(TRACK.APPROVAL_FLOW, 'Apply Flow', { flowId, itemType });
      successToast({ messageKey: 'APPROVAL_FLOWS.APPLY_SUCCESS' });
      setActiveModal(null);
      await refreshApprovalFlow();
      onAttachmentComplete();
    },
    onError: async (error) => {
      errorToast({ httpResponseOrError: error });
      await refreshApprovalFlow();
    },
  });

  const {
    mutate: createAssignment,
    mutateAsync: createAssignmentPromise,
    isPending: isAssigning,
  } = useApiCreateApprovalFlowAssignmentMutation({
    onSuccess: async () => {
      doTrack(TRACK.APPROVAL_FLOW, futureStep ? 'Approve Step' : 'Approve Final Step', {
        flowId: approvalFlow?.id,
        stepPosition: nextStep?.position,
        lockRequired: nextStep?.lockRequired,
      });
      successToast({ messageKey: 'APPROVAL_FLOWS.PROMOTE_SUCCESS' });
      setActiveModal(null);
      await refreshApprovalFlow();
      await queryClient.invalidateQueries({
        queryKey: GetVersionHistoriesQueryKey({}).slice(0, 2),
      });
    },
    onError: async (error) => {
      errorToast({ httpResponseOrError: error });
      await refreshApprovalFlow();
    },
  });

  const { lockAndVersion, hasLockingFailedPreviouslyDueToGlobalItems } = useLockAndVersion<
    ApprovalFlowAssignmentOutputV1 | undefined
  >({
    itemId: workbookId,
    onBeforeRedirect: () => {
      if (!approvalFlow || !nextStep) {
        return Promise.resolve(undefined);
      }
      return createAssignmentPromise([{ stepId: nextStep.id, itemId: workbookId }, { flowId: approvalFlow.id }]);
    },
    closeModal: () => setActiveModal(null),
    source: 'approvalFlow',
  });

  const applyApprovalFlow = async (flow: WorkbookApprovalFlow | null): Promise<void> => {
    if (flow) {
      createApprovalFlowAttachment([{ itemId: workbookId }, { flowId: flow.id }]);
    }
  };

  const approveStep = async (step: NonNullable<typeof nextStep>, versionState?: VersionState): Promise<void> => {
    if (holdsFinalApprovalForContent && affectedContentCount > 0) {
      errorToast({ messageKey: 'APPROVAL_FLOWS.NON_PRODUCTION_CONTENT.BLOCK_APPROVAL' });
      setActiveModal(null);
      return;
    }
    const needsLocking = step.lockRequired && !isLocked;
    if (needsLocking && versionState) {
      try {
        await lockAndVersion(versionState);
        return;
      } catch (error) {
        errorToast({ httpResponseOrError: error });
        return;
      }
    }
    if (approvalFlow) {
      createAssignment([{ stepId: step.id, itemId: workbookId }, { flowId: approvalFlow.id }]);
    }
  };

  const isResolvingProductionStatus = isAssigning || (isFetching && !hasResult);

  return {
    approvalFlow,
    approvalFlows,
    nextStep,
    futureStep,
    showApplyBtn,
    showStepDropdown,
    approved,
    activeModal,
    approvalFlowsEnabled,
    isLocked,
    itemType,
    isInProd,
    isAttaching,
    isAssigning,
    isResolvingProductionStatus,
    hasLockingFailedPreviouslyDueToGlobalItems,
    applyApprovalFlow,
    approveStep,
    openApply: () => {
      attachmentSucceeded.current = false;
      onOpenApply();
      doTrack(TRACK.APPROVAL_FLOW, 'Open Apply Flow Modal', { itemType });
      setActiveModal('FLOW_MODAL');
    },
    openApprove: () => setActiveModal('STEP_MODAL'),
    openViewSteps: () => {
      doTrack(TRACK.APPROVAL_FLOW, 'View Flow Steps', {
        flowId: approvalFlow?.id,
        canApprove: Boolean(nextStep?.permissions.write),
      });
      setActiveModal('FLOW_MODAL');
    },
    closeModal: () => setActiveModal(null),
    didAttachFlow: () => attachmentSucceeded.current,
  };
};
